// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useTimeline } from '@/contexts/timeline-context';
import { Box, Typography } from '@mui/material';
import { AxisScale } from '@visx/axis';
import { BarRounded } from '@visx/shape';
import { DateTime } from 'luxon';
import { MouseEventHandler } from 'react';

interface Props {
  /**
   * The y-coordinate of the segment.
   */
  y: number;

  /**
   * The height of the segment.
   */
  height: number;

  /**
   * The date scale used by the timeline containing the segment.
   */
  scale: AxisScale<number>;

  /**
   * The start date of the segment.
   */
  start: DateTime;

  /**
   * The end date of the segment.
   */
  end: DateTime;

  /**
   * The label of the segment.
   */
  label: string;

  /**
   * A boolean indicating whether the project represented by the segment is
   * archived.
   */
  isArchived?: boolean;

  /**
   * A callback fired when the mouse enters the segment.
   */
  onMouseMove?: MouseEventHandler<SVGElement>;

  /**
   * A callback fired when the mouse leaves the segment.
   */
  onMouseLeave?: MouseEventHandler<SVGElement>;
}

/**
 * A timeline segment that represents a single project. The segment is divided
 * into past and future sections, with the past section colored in the provided
 * color and the future section colored in a lighter shade of the same color
 * with a diagonal pattern.
 */
export function ProjectTimelineSegment(props: Props) {
  const { palette } = useTimeline();

  const today = DateTime.now().startOf('day');
  const pastX = props.scale(props.start.toJSDate()) ?? 0;
  const futureX = props.scale(DateTime.max(today, props.start)) ?? 0;
  const endX = props.scale(props.end.toJSDate()) ?? 0;
  const pastWidth = Math.max(0, Math.min(endX, futureX) - pastX);
  const futureWidth = Math.max(0, endX - futureX);
  const textX = Math.max(0, pastX);
  const textWidth = endX - textX;
  const isFuture = props.start > DateTime.now().startOf('day');
  const isComplete = props.end < DateTime.now().endOf('day');
  const futureFill = props.isArchived
    ? palette.disabled.diagonal
    : palette.project.diagonal;

  const pastFill = props.isArchived
    ? palette.disabled.solid
    : palette.project.solid;

  return (
    <g onMouseMove={props.onMouseMove} onMouseLeave={props.onMouseLeave}>
      <BarRounded
        right
        left={isFuture}
        radius={4}
        x={futureX}
        y={props.y}
        fill={futureFill}
        width={futureWidth}
        height={props.height}
      />
      <BarRounded
        left
        right={isComplete}
        radius={4}
        x={pastX}
        y={props.y}
        width={pastWidth}
        height={props.height}
        fill={pastFill}
      />
      <foreignObject
        x={textX}
        y={props.y}
        height={props.height}
        width={textWidth}
      >
        <Box paddingX={1} height="100%" display="flex" alignItems="center">
          <Typography color="white" variant="body2" whiteSpace="nowrap">
            {props.label}
          </Typography>
        </Box>
      </foreignObject>
    </g>
  );
}
